import React from 'react';
import immutable from 'immutable';
import ReactSelectize from 'react-selectize';
import ConstValues from 'utils/const';
import CrmSyncActions from 'actions/crm/CrmSyncActions';
import 'styles/call-card/sync-crm/CallSyncCRM.styl';
import classNames from 'classnames';
import LoadingGif from 'images/card/Loading.gif';

/**
 * CallSyncCRMContactLeadSearchInputBox is a component that allows any user / manager to search salesforce
 * contacts/leads within the user's salesforce account
 */
export default class CallSyncCRMContactLeadSearchInputBox extends React.Component {
  constructor() {
    super();
    this.state = {
      // array of objects with keys (value, label, id, type)
      options: [],
      // previous search term
      previousSearch: '',
      // current search term
      search: '',
      // queuedSearch stores a chosen search term that's queued due to rate-limit of the backend
      queuedSearch: null,
      // runningSearch stores the current search term that's sent to the backend
      runningSearch: null,
    };
    this.filterOptions = this.filterOptions.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onOpenChange = this.onOpenChange.bind(this);
    this.onSearchChange = this.onSearchChange.bind(this);
    this.onValuesChange = this.onValuesChange.bind(this);
    this.renderNoResultsFound = this.renderNoResultsFound.bind(this);
  }

  /**
   * Callback when this component will receive new props including a list of contacts/leads
   * @param nextProps {object} Props including a new list of contacts/leads and related metadata
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    const options = this.optionsFrom(nextProps);
    if (options) {
      this.setState({options: options});
    }
    this.runQueuedSearchIfNecessaryOrClearSearchState(nextProps);
  }

  /**
   * Return the current options for contact-and-leads-search-input-box given what's chosen in the account / opportunity
   * search input box
   * @param {object} nextProps New props
   */
  optionsFrom(nextProps) {
    let options;
    if (
      nextProps.accountAndOpportunitiesValues &&
      nextProps.accountAndOpportunitiesValues.count() > 0
    ) {
      // User chose a account / opportunity in the Related-To search input box
      let contactsOptions =
        nextProps.contactsOptions !== this.props.options
          ? nextProps.contactsOptions
          : this.props.options;
      if (
        accountOrOpportunityNameFromRelatedTo(nextProps.accountAndOpportunitiesValues.first())
          .type === 'account'
      ) {
        contactsOptions = contactsOptions.filter(contactOption => contactOption.type === 'contact');
      }
      options = contactsOptions
        .map(option => {
          return {
            value: option.name,
            label: option.name,
            id: option.id,
            option: option,
            selectable: false,
          };
        })
        .toArray();
    } else {
      // User hasn't chosen any account / opportunity in the Related-To search input box
      if (nextProps.options !== this.props.options) {
        options = nextProps.options
          .map(option => {
            return {
              value: option.name,
              label: option.name,
              id: option.id,
              option: option,
              selectable: false,
            };
          })
          .toArray();
        options = this.optionsWithRemainingIndicator(options, nextProps);
      }
    }
    return options;
  }

  /**
   * Run this.state.queuedSearch if necessary, e.g., if this.state.queuedSearch is not null and if nextProps.loading is
   * false. This function should also set queuedSearch and runningSearch back to null if this.state.queuedSearch is
   * equal to this.state.runningSearch
   * @param {Object} nextProps
   */
  runQueuedSearchIfNecessaryOrClearSearchState(nextProps) {
    if (this.props.loading && !nextProps.loading) {
      if (this.state.queuedSearch && this.state.queuedSearch !== this.state.runningSearch) {
        this.prefixSearch(this.state.queuedSearch);
      } else if (this.state.queuedSearch && this.state.queuedSearch === this.state.runningSearch) {
        this.setState({queuedSearch: null, runningSearch: null});
      } else {
        this.setState({runningSearch: null});
      }
    }
  }

  /**
   * Perform a prefix search using `term`
   * @param {string/null} term Search term used for the prefix search
   */
  prefixSearch(term) {
    this.setState({runningSearch: term});
    setImmediate(() => {
      const contactAccountId = accountIdFromRelatedTo(
        this.props.accountAndOpportunitiesValues.first()
      );
      CrmSyncActions.prefixSearchContactsAndLeads(term, contactAccountId);
    });
  }

  /**
   * Return a new array of options with the last element appended as a remaining-number-of-results-indicator
   * @param {Array.<object>} options Array of current contact / lead options
   * @param {object} nextProps New props passed down from the parent container
   */
  optionsWithRemainingIndicator(options, nextProps) {
    let optionsList = immutable.List(options);
    const numRemaining = nextProps.total - nextProps.numReturned;
    if (numRemaining > 0 && optionsList.count() > 0) {
      const indicatorOption = {
        value: numRemaining,
        label: `${numRemaining}`,
        option: {
          type: ConstValues.remainingIndicatorOptionType,
          value: numRemaining,
        },
      };
      optionsList = optionsList.push(indicatorOption);
    }
    return optionsList.toArray();
  }

  /**
   * Whenever the user changes the search term, we initiate a backend API request to fetch the contacts/leads *OR*
   * queue the search term in a temporary front-end storage and then initiate it when the current search is done
   * @param term Current search term
   */
  onSearchChange(term) {
    let state = {search: term};
    if (term.length >= ConstValues.minimalNumberOfCharactersToTriggerSearch) {
      if (!this.props.loading) {
        this.prefixSearch(term);
      } else {
        state.queuedSearch = term;
      }
    }
    if (term.length > 0) {
      // Sometimes this.state.search could be cleared by an onBlur event so we only update state.previousSearch if term
      // is a valid string
      state.previousSearch = term;
    }
    this.setState(state);
  }

  /**
   * Whenever the user selects a value, we replace the current value if it's already selected or populate
   * `this.props.values` with the new item's value
   * @param {Array.<object>} items Array of selectize items that includes a contact / lead related option
   */
  onValuesChange(items) {
    const lastItem = items[items.length - 1];
    if (
      lastItem &&
      lastItem.option &&
      lastItem.option.type !== ConstValues.remainingIndicatorOptionType
    ) {
      if (lastItem.option.type === 'contact') {
        // Reset the "Related-To" input box to an account
        CrmSyncActions.populateAccountForContact(lastItem.option);
      } else if (lastItem.option.type === 'lead') {
        // Reset the "Related-To" input box to nothing if we select a lead here
        CrmSyncActions.clearAccountsAndOpportunitiesValues();
      }
      CrmSyncActions.updateCurrentPrefixSearchContactsAndLeadsValues([lastItem]);
    } else {
      CrmSyncActions.updateCurrentPrefixSearchContactsAndLeadsValues([]);
    }
    if (items.length > 0) {
      this._select.blur();
    }
  }

  /**
   * Mark the last option / remaining-indicator as not selectable while other options as selectable
   * @param {Array.<object>} options Array of contact / lead options
   * @param {Array.<object>} values Currently selected values
   * @param {String} search Current search term
   */
  filterOptions(options, values, search) {
    let optionsList = immutable.List(options);
    optionsList = optionsList.map((item, index) => {
      if (item.option.type === ConstValues.remainingIndicatorOptionType) {
        item.selectable = false;
      } else {
        item.selectable = true;
      }
      return item;
    });
    if (hasChosenAccount(this.props)) {
      optionsList = optionsList.filter(item => item.option.type === 'contact');
    }
    const searchLowerCase = search.toLowerCase();
    optionsList = optionsList.filter(item => {
      const label = item.label.toLowerCase();
      return (
        item.option.type === ConstValues.remainingIndicatorOptionType ||
        label.includes(searchLowerCase)
      );
    });
    if (
      optionsList.count() === 1 &&
      optionsList.first().option.type === ConstValues.remainingIndicatorOptionType
    ) {
      optionsList = immutable.List();
    }
    return optionsList.toArray();
  }

  renderNoResultsFound(item, search) {
    let noResultFound = 'No results found';
    if (this.props.loading || this.state.runningSearch || this.state.queuedSearch) {
      return (
        <div className="no-results-found">
          <span>Searching</span> <img alt="loading" src={LoadingGif} style={{height: '12px'}} />
        </div>
      );
    } else if (search.length >= ConstValues.minimalNumberOfCharactersToTriggerSearch) {
      const accountOrOppName = accountOrOpportunityNameFromRelatedTo(
        this.props.accountAndOpportunitiesValues.first()
      );
      if (accountOrOppName !== null) {
        noResultFound = `No matches related to ${accountOrOppName}. Clear the Related To field if you wish to clear this filter.`;
      }
    } else {
      noResultFound = 'Start typing to find options';
    }
    return <div className="no-results-found">{noResultFound}</div>;
  }

  onBlur(event) {
    this.clearOptionsWhenNecessary();
    this.setState({runningSearch: null, queuedSearch: null});
    CrmSyncActions.cancelPrefixSearchContactsAndLeads();
  }

  onFocus(event) {
    this.clearOptionsWhenNecessary();
  }

  clearOptionsWhenNecessary() {
    if (!(hasChosenAccountOrOpportunityValue(this.props) || this.props.values.length > 0)) {
      // no value is chosen for either account/opportunity or contact/lead
      CrmSyncActions.clearContactsAndLeadsOptions();
    }
  }

  onOpenChange(open) {
    if (open && this.props.values.length > 0) {
      // Clear current value and show the search term with previous results
      CrmSyncActions.clearCurrentPrefixSearchContactsAndLeadsValues();
      this.setState({
        previousSearch: '',
        search: this.state.previousSearch + this.state.search,
      });
    }
  }

  render() {
    const selectClassnames = classNames('left-form-control', 'long-form-control');
    return (
      <ReactSelectize.MultiSelect
        placeholder={this.props.placeholder}
        options={this.state.options}
        values={this.props.values}
        className={selectClassnames}
        search={this.state.search}
        onSearchChange={this.onSearchChange}
        renderOption={renderItemOption}
        renderValue={renderItemValue}
        onValuesChange={this.onValuesChange}
        filterOptions={this.filterOptions}
        closeOnSelect={true}
        onOpenChange={this.onOpenChange}
        renderNoResultsFound={this.renderNoResultsFound}
        onBlur={this.onBlur}
        onFocus={this.onFocus}
        ref={select => (this._select = select)}
      />
    );
  }
}

/**
 * Custom item rendering function to render a one-line selectize value using `item.option` based on its type
 * @param item {object} Selectize item that includes a contact / lead related option
 */
function renderItemValue(item) {
  let itemValueSpan;
  const option = item.option;
  if (option.type === 'contact') {
    // Render `option.name`
    itemValueSpan = (
      <span style={{verticalAlign: 'middle'}}>
        {option.name} <span className="capitalize">({option.type})</span>
      </span>
    );
  } else if (option.type === 'lead') {
    // Render `option.name - option.company`
    itemValueSpan = (
      <span style={{verticalAlign: 'middle'}}>
        {option.name} - {option.company} <span className="capitalize">({option.type})</span>
      </span>
    );
  }
  return <div className="simple-value">{itemValueSpan}</div>;
}

/**
 * Custom item rendering function to render a one-line or multi-line selectize option based on `item`
 * @param item {object} Selectize item that includes contact / lead related option
 */
function renderItemOption(item) {
  const option = item.option;
  if (option.type === 'lead') {
    return (
      <div className="crm-entry">
        <span className="lead">
          <span className="name">
            {option.name} - {option.company}
          </span>{' '}
          <span className="type">{option.type}</span>
        </span>
      </div>
    );
  } else if (option.type === 'contact') {
    return (
      <div className="crm-entry">
        <span className="contact">
          <span className="name first-line">{option.name}</span>{' '}
          <span className="type first-line">{option.type}</span>
          <br />
          <span className="name italic">{option.account.name}</span>{' '}
          <span className="type">{option.account.type}</span>
        </span>
      </div>
    );
  } else if (option.type === ConstValues.remainingIndicatorOptionType) {
    return (
      <div className="crm-entry unselectable">
        <span className="remaining-indicator">
          <span>{option.value} other results. Type more to narrow search</span>
        </span>
      </div>
    );
  }
}

/** Return the CRM account/opportunity name chosen by the current user */
function accountOrOpportunityNameFromRelatedTo(accountOrOpportunity) {
  if (accountOrOpportunity) {
    return accountOrOpportunity.option.name;
  }
  return null;
}

/** Return true if an account or an opportunity has been chosen */
function hasChosenAccountOrOpportunityValue(props) {
  return accountIdFromRelatedTo(props.accountAndOpportunitiesValues.first()) !== null;
}

function hasChosenAccount(props) {
  return (
    hasChosenAccountOrOpportunityValue(props) &&
    props.accountAndOpportunitiesValues.first().option.type === 'account'
  );
}

/** Return the CRM contact-account-id currently chosen by the user */
function accountIdFromRelatedTo(accountOrOpportunity) {
  if (accountOrOpportunity) {
    if (accountOrOpportunity.option.type === 'opportunity') {
      return accountOrOpportunity.option.account.id;
    } else if (accountOrOpportunity.option.type === 'account') {
      return accountOrOpportunity.option.id;
    }
  }
  return null;
}
